import React, { cloneElement, useEffect, useRef, useState } from 'react';
import { bool, node, object, string } from 'prop-types';
import { Button as MuiButton, ClickAwayListener, Grow, Popper, useTheme, InputLabel, FormControl } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ExpandMoreRounded } from '@mui/icons-material';

const Button = styled(MuiButton, {
  shouldForwardProp: prop => prop !== 'open' || prop !== 'disabled',
})(({ open }) => ({
  '& svg#expand-icon': {
    transition: 'transform 0.25s ease',
    ...(open && {
      transform: 'rotate(180deg)',
    }),
  },
}));

/* istanbul ignore next */
const MenuItemWrapper = ({
  name,
  label,
  isFilterActive,
  disabled,
  id,
  children,
  popperPlacement,
  buttonProps,
  fullWidth,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  return (
    <FormControl fullWidth={fullWidth}>
      {label && (
        <InputLabel htmlFor={id} shrink>
          {label}
        </InputLabel>
      )}
      <Button
        ref={anchorRef}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        filterActive={isFilterActive}
        open={open}
        onClick={handleToggle}
        variant="outlined"
        color="secondary"
        disabled={disabled}
        id={id}
        endIcon={<ExpandMoreRounded id="expand-icon" />}
        {...buttonProps}
        size="small"
        sx={{ width: fullWidth ? '100%' : 'auto', justifyContent: 'space-between' }}
      >
        {name}
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement={popperPlacement}
        transition
        sx={{ zIndex: theme.zIndex.popover }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
          >
            <div>
              <ClickAwayListener onClickAway={handleClose}>
                <div>
                  {cloneElement(children, { setOpen })}
                </div>
              </ClickAwayListener>
            </div>
          </Grow>
        )}
      </Popper>
    </FormControl>
  );
};

export default MenuItemWrapper;

MenuItemWrapper.propTypes = {
  name: string.isRequired,
  label: string,
  isFilterActive: bool,
  disabled: bool,
  id: string.isRequired,
  children: node.isRequired,
  popperPlacement: string,
  buttonProps: object,
  fullWidth: bool,
};

MenuItemWrapper.defaultProps = {
  label: '',
  isFilterActive: false,
  disabled: false,
  popperPlacement: 'bottom-end',
  buttonProps: null,
  fullWidth: false,
};
